import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Select, { components } from "react-select";
import axios from 'axios';
import { Link } from "react-router-dom";

const AddPackage = ({ show, onClose, onSuccess }) => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const branch_id = parseInt(localStorage.getItem("branch_id"), 10);

    const toTitleCase = (str) => str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());

    const sanitizeAmount = (val, maxLength = 10) => {
        return val.replace(/\D/g, '').slice(0, maxLength);
    };

    const [specification, setSpecification] = useState([{ id: Date.now(), specification: '' }]);
    const [packageName, setPackageName] = useState('');
    const [sellingAmount, setSellingAmount] = useState('');
    const [productionAmount, setProductionAmount] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedTimeline, setSelectedTimeline] = useState(null);
    const [productOptions, setProductOptions] = useState([]);
    const [timelineOptions, setTimelineOptions] = useState([]);

    const [errors, setErrors] = useState({});
    const [itemcatErrors, setItemcatErrors] = useState({});

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await axios.get(`${baseURL}/products/grouped-by-product-category?branch_id=${branch_id}`);
                const groupedOptions = response.data.data.map(category => ({
                    label: category.product_category_name,
                    options: category.products.map(product => ({
                        value: product.product_id,
                        label: product.product_name
                    }))
                }));
                setProductOptions(groupedOptions);
            } catch (error) {
                console.error("Failed to fetch products:", error);
            }
        };

        const fetchTimelines = async () => {
            try {
                const response = await axios.get(`${baseURL}/package-timeline/active`);
                const formattedTimelines = response.data.data.map(item => ({
                    value: item.sno,
                    label: item.packagetimeline_name,
                    days: item.days,
                    all_days: item.all_days,
                    ...item
                }));
                setTimelineOptions(formattedTimelines);
            } catch (error) {
                console.error("Failed to fetch package timelines:", error);
            }
        };

        fetchProducts();
        fetchTimelines();
    }, [baseURL]);

    const renderDaysFromTimeline = () => {
        const days = [];
        const weekdays = [
            { key: 'sunday', label: 'Sun' },
            { key: 'monday', label: 'Mon' },
            { key: 'tuesday', label: 'Tue' },
            { key: 'wednesday', label: 'Wed' },
            { key: 'thursday', label: 'Thu' },
            { key: 'friday', label: 'Fri' },
            { key: 'saturday', label: 'Sat' },
        ];

        if (selectedTimeline?.all_days === 0) return "All Days";

        weekdays.forEach(day => {
            if (selectedTimeline?.[day.key] === '0') {
                days.push(day.label);
            }
        });

        return days.length > 0 ? days.join(', ') : "No active days";
    };

    const handleAddConfig = () => {
        setSpecification([...specification, { id: Date.now(), specification: '' }]);
    };

    const handleDeleteConfig = (id) => {
        setSpecification(specification.filter(config => config.id !== id));
    };

    const Group = (props) => (
        <div style={customStyles}>
            <components.Group {...props} />
        </div>
    );

    const customStyles = {
        groupHeading: (provided) => ({
            ...provided,
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#333',
        }),
    };

    const validateForm = () => {
        const formErrors = {};
        const itemcatValidationErrors = {};

        if (!packageName.trim()) {
            formErrors.package_name = "Package Name is required";
        }

        if (!sellingAmount || isNaN(sellingAmount) || parseFloat(sellingAmount) <= 0) {
            formErrors.selling_amount = "Valid Selling Amount is required";
        }

        if (!productionAmount || isNaN(productionAmount) || parseFloat(productionAmount) <= 0) {
            formErrors.production_amount = "Valid Oneday Selling Amount is required";
        }

        if (!selectedOption) {
            formErrors.product_category_id = "Product Category is required";
        }

        if (!selectedTimeline) {
            formErrors.package_timeline = "Package Timeline is required";
        }

        specification.forEach(config => {
            if (!config.specification.trim()) {
                itemcatValidationErrors[config.id] = "Specification is required";
            }
        });

        setErrors(formErrors);
        setItemcatErrors(itemcatValidationErrors);

        return Object.keys(formErrors).length === 0 && Object.keys(itemcatValidationErrors).length === 0;
    };

    const handleFormSubmit = async () => {
        const isValid = validateForm();
        if (!isValid) return;

        const specificationsArray = specification
            .map(config => toTitleCase(config.specification.trim()).slice(0, 150))
            .filter(spec => spec !== '');

        if (specificationsArray.length === 0) {
            alert("Please add at least one specification.");
            return;
        }

        // Get branch_id and user_id from localStorage
        const branchId = localStorage.getItem("branch_id");
        const userId = localStorage.getItem("user_id");

        const packageData = {
            package_name: toTitleCase(packageName.trim()).slice(0, 50),
            product_amount: productionAmount,
            selling_amount: sellingAmount,
            products_id: selectedOption.value,
            products_timeline: selectedTimeline.value,
            specification: specificationsArray,
            branch_id: parseInt(branchId),   // include branch_id
            created_by: parseInt(userId)      // include created_by
        };

        try {
            const response = await axios.post(`${baseURL}/manage-package`, packageData);
            if (response.status === 201) {
                onClose();
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error("Error creating package:", error.response?.data || error);
        }
    };

    return (
        <Modal show={show} size="lg">
            <div className="modal-content border border-red rounded-xxl">
                <div className="modal-header" style={{ backgroundColor: '#3ab384' }}>
                    <h3 className="modal-title text-white">Create Package</h3>
                    <Button variant="" type="button" className="close px-6 py-2" data-dismiss="modal" onClick={onClose}>
                        <span className="text-white">×</span>
                    </Button>
                </div>
                <div className="modal-body">
                    <div className="row mb-2">
                        <div className="col-lg-4 mb-4">
                            <label className="text-black font-w500 fs-7">Package Name <span className="required">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                value={packageName}
                                maxLength={50}
                                onChange={(e) => setPackageName(toTitleCase(e.target.value).slice(0, 50))}
                                placeholder="Enter Package Name"
                            />
                            {errors.package_name && <span className="text-danger">{errors.package_name}</span>}
                        </div>

                        <div className="col-lg-4 mb-4">
                            <label className="text-black font-w500 fs-7">Products <span className="required">*</span></label>
                            <Select
                                placeholder='Select Product'
                                value={selectedOption}
                                onChange={setSelectedOption}
                                options={productOptions}
                                components={{ Group }}
                                styles={customStyles}
                            />
                            {errors.product_category_id && <span className="text-danger">{errors.product_category_id}</span>}
                        </div>

                        <div className="col-lg-4 mb-4">
                            <label className="text-black font-w500 fs-7">Package Timeline <span className="required">*</span></label>
                            <Select
                                placeholder='Select Package Timeline'
                                value={selectedTimeline}
                                onChange={setSelectedTimeline}
                                options={timelineOptions}
                            />
                            {errors.package_timeline && <span className="text-danger">{errors.package_timeline}</span>}
                        </div>

                        {selectedTimeline && (
                            <div className="col-lg-4 mb-4 text-center">
                                <label className="text-black font-w500 fs-6">Deliverables - {selectedTimeline.days}</label>
                                <div className='d-block'>
                                    <label className="text-danger font-w600 fs-6">{renderDaysFromTimeline()}</label>
                                </div>
                            </div>
                        )}

                        <div className="col-lg-4 mb-4">
                            <label className="text-black font-w500 fs-7">Selling Amount <span className="required">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                value={sellingAmount}
                                maxLength={10}
                                onChange={(e) => setSellingAmount(sanitizeAmount(e.target.value, 10))}
                                placeholder="Enter Selling Amount"
                            />
                            {errors.selling_amount && <span className="text-danger">{errors.selling_amount}</span>}
                        </div>

                        <div className="col-lg-4 mb-4">
                            <label className="text-black font-w500 fs-7">Oneday Selling Amount <span className="required">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                value={productionAmount}
                                maxLength={10}
                                onChange={(e) => setProductionAmount(sanitizeAmount(e.target.value, 10))}
                                placeholder="Enter Oneday Selling Amount"
                            />
                            {errors.production_amount && <span className="text-danger">{errors.production_amount}</span>}
                        </div>
                    </div>

                    <div className='row mb-3'>
                        <div className='col-lg-12 mb-3'>
                            <div className="d-flex align-items-center">
                                <label className="fs-5 text-red font-w600">Add Specifications</label>
                            </div>
                            <div className="mb-3">
                                {specification.map(config => (
                                    <div key={config.id}>
                                        <div className="row">
                                            <div className='col-lg-10'>
                                                <label className="text-black font-w500 fs-7">Specification <span className="required">*</span></label>
                                                <textarea
                                                    className="form-control mt-2"
                                                    value={config.specification}
                                                    maxLength={150}
                                                    placeholder="Enter Specification"
                                                    onChange={(e) => {
                                                        const updated = specification.map(item =>
                                                            item.id === config.id
                                                                ? { ...item, specification: toTitleCase(e.target.value).slice(0, 150) }
                                                                : item
                                                        );
                                                        setSpecification(updated);
                                                    }}
                                                />
                                                {itemcatErrors[config.id] && <span className="text-danger">{itemcatErrors[config.id]}</span>}
                                            </div>
                                            <div className="col-lg-2 mb-1 px-1 py-1">
                                                {specification.length > 1 && (
                                                    <button
                                                        className="btn btn-danger mt-4 px-2 py-2"
                                                        onClick={() => handleDeleteConfig(config.id)}
                                                        data-bs-toggle="tooltip"
                                                        data-bs-placement="top"
                                                        title="Delete"
                                                    >
                                                        <i className="fa-solid fa-trash-can fs-6 mt-1 text-white"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mb-1 mt-1">
                                <button
                                    className="btn btn-sm btn-primary"
                                    onClick={handleAddConfig}
                                >
                                    <span><i className="fa-solid fa-plus fs-6"></i></span>
                                    <span className='ms-2 fs-7'>Add More</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-end gap-3 mt-4 mb-4">
                        <Link type="submit" className="btn btn-sm btn-primary" onClick={handleFormSubmit}>Create Package <i className="fas fa-angle-double-right ms-2"></i></Link>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AddPackage;
